import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, TrendingDown, Zap, RefreshCw, CheckCircle, Server } from 'lucide-react';

/**
 * AvailableSavingsHero — CAST-AI-style "Available Savings" headline for the
 * Node Optimization page. SYSTEM-OF-RECORD = the in-cluster engine: the
 * savings + node plan come from the engine's RebalancePlan (preview = actual),
 * NOT the backend recommender. This is what keeps the headline coherent with
 * what will actually execute (the engine never proposes unsafe drains).
 *
 *   current $/mo ──▶ optimized $/mo     −$X/mo (−Y%)     N ▶ M nodes
 *   [ Apply rebalance ]
 *
 * Data:
 *   engine plan  → GET /node-optimization/rebalance-plan  (projected_monthly_usd,
 *                  current_nodes, optimized_nodes, available, reason)
 *   current spend→ GET /node-optimization/plan  (current_cost.monthly only — the
 *                  real fleet cost; the backend's *optimized* plan is NOT used)
 * CTA:
 *   Apply rebalance→ POST /node-optimization/rebalance-plan/apply (RebalanceClaim)
 */
const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Capacity policies in ascending-savings order, matching the wording used by
// the Configuration > Capacity selector so the two surfaces never disagree.
const POLICY_ORDER = [
  { key: 'on-demand', label: 'On-Demand', blurb: 'Stable price, no interruptions' },
  { key: 'mixed', label: 'Mixed', blurb: 'Spot where safe, on-demand fallback' },
  { key: 'spot', label: 'Spot', blurb: 'Lowest cost, interruptible' },
];

const AvailableSavingsHero = ({ clusterId }) => {
  const [engine, setEngine] = useState(null);    // engine RebalancePlan (SoR)
  const [backend, setBackend] = useState(null);  // backend plan — ONLY for current cost + node count
  const [estimate, setEstimate] = useState(null); // agent-only funnel: simulated savings when the engine isn't installed
  const [policies, setPolicies] = useState(null); // agent-only funnel: per-capacity-policy cost comparison
  const [engineMissing, setEngineMissing] = useState(false); // engine reported no plan (agent-only install)
  const [potential, setPotential] = useState(null); // whole-fleet opportunity incl. nodes not yet under Karpenter
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchPlan = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    try {
      // BOTH numbers come from the LIVE fleet source (the same endpoint the
      // Rebalancer panel uses): current = live managed nodes × Infracost
      // catalog; target = the latest engine plan's fleet. The old
      // /node-optimization/plan baseline was the RETIRED backend optimizer's
      // last doc — frozen forever under SoR (the "hero always says $268" bug).
      // Both tolerate failure: without the .catch on the first call, ANY
      // rejection skipped setBackend entirely and left the hero stuck on
      // "Syncing fleet data" forever even though the fleet feed was fine.
      const [eng, cfg] = await Promise.all([
        axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-plan`).catch(() => null),
        axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-configuration`).catch(() => null),
      ]);
      setEngine(eng?.data || null);
      setBackend(cfg?.data || null);
      // FUNNEL (agent-only installs): when the ENGINE isn't publishing plans
      // (reason no_plan / preview_unavailable — i.e. the rebalancer isn't
      // installed yet), fall back to the backend SIMULATOR's estimate so a
      // fresh onboard sees "here's what you could save" instantly instead of
      // a false "right-sized". Freshness-guarded (<2h; the scheduler
      // recomputes ~10min) so a stale frozen doc can never masquerade as a
      // live number — the exact bug that retired this endpoint from the
      // headline path. Clearly labeled a simulation in the UI; never an
      // apply surface.
      // A failed request is UNKNOWN, not "absent" — only a server-reported
      // reason counts, so a transient error can't flip us into the funnel.
      const engineAbsent = ['no_plan', 'preview_unavailable'].includes(eng?.data?.reason);
      setEngineMissing(engineAbsent);
      if (engineAbsent) {
        try {
          const sim = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/plan`);
          const d = sim.data || {};
          const ageMs = d.timestamp ? Date.now() - new Date(d.timestamp).getTime() : Infinity;
          const fresh = ageMs < 2 * 3600 * 1000;
          const saves = d?.savings?.monthly || 0;
          if (fresh && d.simulation_passed && saves > 0) {
            setEstimate({
              currentMonthly: d.current_cost?.monthly || 0,
              optimizedMonthly: d.optimized_cost?.monthly || 0,
              savingsMonthly: saves,
              savingsPct: d.savings?.percent || 0,
              // Node counts make the SCOPE explicit. The simulator prices every
              // node the agent reports, while the fleet card below prices only
              // Karpenter-managed ones — without these counts the two cards
              // look like they disagree about the same cluster.
              currentNodes: d.current_nodes || 0,
              optimizedNodes: d.optimized_nodes || 0,
            });
            // Capacity-policy comparison (CAST-style): the simulator already
            // computes a plan per policy, so show what each one would cost
            // instead of a single number the user can't put in context.
            // Additive + best-effort: a failure here just hides the table.
            try {
              const all = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/plans`);
              const rows = POLICY_ORDER
                .map(({ key, label, blurb }) => {
                  const p = (all.data?.plans || {})[key];
                  // Only trust a policy whose own simulation passed — a plan
                  // where pods don't fit is not a real option to offer.
                  if (!p || !p.simulation_passed) return null;
                  // IDENTITY GUARD: the backend reader falls back to ANY
                  // policy's doc when the requested one is missing, so all
                  // three keys can resolve to the SAME doc and render as three
                  // identical rows labelled on-demand/mixed/spot. Trust a row
                  // only when the doc itself claims that policy.
                  if (p.spot_policy !== key) return null;
                  // Per-row freshness, same 2h rule as the headline: a policy
                  // pass that failed mid-loop leaves a stale sibling behind.
                  const rowAge = p.timestamp ? Date.now() - new Date(p.timestamp).getTime() : Infinity;
                  if (rowAge >= 2 * 3600 * 1000) return null;
                  return {
                    key, label, blurb,
                    optimizedMonthly: p.optimized_cost?.monthly || 0,
                    savingsMonthly: p.savings?.monthly || 0,
                    savingsPct: p.savings?.percent || 0,
                  };
                })
                .filter(Boolean);
              setPolicies(rows.length > 1 ? rows : null);
            } catch { setPolicies(null); }
            // WHOLE-FLEET opportunity. The plan above is deliberately scoped to
            // what the rebalancer can act on with today's Karpenter coverage —
            // nodes outside a NodePool can't be removed yet, so their headroom
            // is excluded from the deliverable number (that number gates
            // execution and must stay honest). But OptOps CAN bring those nodes
            // under management (CA→Karpenter migration, then CLM live-migrates
            // workloads with no downtime), so the larger figure is real and is
            // what the funnel should promise — shown alongside, never instead.
            try {
              const av = await axios.get(`${API}/v2/clusters/${clusterId}/available-savings`);
              const scenarios = av.data?.scenarios || {};
              const best = Object.values(scenarios).reduce((acc, s) => {
                const before = s?.priceBefore?.monthly;
                const after = s?.priceAfter?.monthly;
                if (!(before > 0) || !(after >= 0)) return acc;
                const save = before - after;
                return save > (acc?.savingsMonthly || 0)
                  ? { savingsMonthly: save, optimizedMonthly: after, currentMonthly: before }
                  : acc;
              }, null);
              setPotential(
                best && best.savingsMonthly > 0
                  ? { ...best, nodeCount: av.data?.currentConfiguration?.nodeCount || 0 }
                  : null
              );
            } catch { setPotential(null); }
          } else {
            setEstimate(null);
            setPolicies(null);
            setPotential(null);
          }
        } catch { setEstimate(null); setPolicies(null); setPotential(null); }
      } else {
        setEstimate(null);
        setPolicies(null);
        setPotential(null);
      }
    } catch {
      setEngine(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchPlan();
    const t = setInterval(fetchPlan, 30000); // live fleet — keep the hero honest
    return () => clearInterval(t);
  }, [fetchPlan]);

  const applyRebalance = async () => {
    if (!window.confirm('Apply this rebalance? Workloads will be moved to the optimized set of nodes with zero downtime.')) return;
    setApplying(true); setMsg(null);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-plan/apply`);
      setMsg({ ok: true, text: 'Rebalance started — this usually takes a few minutes.' });
    } catch (e) {
      setMsg({ ok: false, text: 'Couldn’t start the rebalance — try again.', detail: e?.response?.data?.detail || e.message });
    } finally { setApplying(false); }
  };

  // LIVE numbers from rebalance-configuration: current = live fleet cost,
  // optimized = the engine plan's target fleet cost, savings = the delta —
  // exactly what the Rebalancer panel shows, so the two never disagree.
  const currentMonthly = backend?.current_monthly || 0;
  const optimizedMonthly = backend?.available ? (backend?.target_monthly || 0) : currentMonthly;
  const savingsMonthly = backend?.available ? (backend?.savings_monthly || 0) : 0;
  const savingsPct = backend?.available ? (backend?.savings_pct || 0) : 0;
  const currentNodes = (backend?.current || []).reduce((a, r) => a + (r.qty || 0), 0) || engine?.current_nodes || 0;
  const optimizedNodes = backend?.available
    ? (backend?.target || []).reduce((a, r) => a + (r.qty || 0), 0)
    : currentNodes;
  // Optimal when the engine has no actionable plan (right-sized → nothing to do).
  const optimal = !backend?.available || savingsMonthly <= 0;
  const clusterOptimum = backend?.cluster_optimum;
  // Baseline-relative savings (vs on-demand): the value banked even at
  // optimal packing — a fully-spot cluster shows $0 rebalance headroom but
  // large spot savings. Keeps an optimal cluster from reading "nothing here".
  const spotSavingsMonthly = backend?.spot_savings_monthly || 0;
  const spotSavingsPct = backend?.spot_savings_pct || 0;
  // No fleet data (yet): first paint after navigation, or the fleet feed is
  // syncing. NEVER render this as "$0 / right-sized" — that reads as a
  // (wrong) verdict. Skeleton while loading; an honest syncing state after.
  const noData = !(backend?.current || []).length;

  if (loading && noData) {
    return (
      <Card className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <CardContent className="p-6">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Available savings</p>
          <div className="mt-3 h-8 w-56 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            <div className="hidden sm:block" />
            <div className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Agent-only funnel card: simulated savings, clearly labeled, no apply.
  if (estimate && !backend?.available) {
    const fmt = (v) => `$${Math.round(v).toLocaleString()}`;
    return (
      <Card className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Estimated savings</p>
            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 border-0">simulation</Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-slate-900 dark:text-slate-100">{fmt(estimate.savingsMonthly)}/mo</span>
            <span className="text-sm text-emerald-600 dark:text-emerald-400">−{Math.round(estimate.savingsPct)}%</span>
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">Current</p>
              <p className="mt-1 text-xl font-medium text-slate-900 dark:text-slate-100">{fmt(estimate.currentMonthly)}/mo</p>
              {estimate.currentNodes > 0 && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                  <Server className="h-3 w-3" />{estimate.currentNodes} nodes
                </p>
              )}
            </div>
            <ArrowRight className="hidden sm:block h-4 w-4 text-slate-400" />
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">Optimized (simulated)</p>
              <p className="mt-1 text-xl font-medium text-emerald-700 dark:text-emerald-400">{fmt(estimate.optimizedMonthly)}/mo</p>
              {estimate.optimizedNodes > 0 && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                  <Server className="h-3 w-3" />{estimate.optimizedNodes} nodes
                </p>
              )}
            </div>
          </div>
          {/* Unmanaged headroom. Only shown when the whole-fleet opportunity
              genuinely exceeds what today's coverage can deliver — i.e. some
              nodes aren't under Karpenter yet. The gap is the CTA, not a
              caveat: OptOps migrates those nodes, then CLM moves their
              workloads without downtime. Never replaces the deliverable
              number above; the reader gets both, labelled. */}
          {potential && potential.savingsMonthly > estimate.savingsMonthly + 1 && (
            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Total opportunity across all {potential.nodeCount || estimate.currentNodes} nodes
                </p>
                <p className="text-xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                  {fmt(potential.savingsMonthly)}<span className="text-xs font-normal text-slate-500 dark:text-slate-400">/mo</span>
                </p>
              </div>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                {fmt(estimate.savingsMonthly)}/mo of that is reachable with your current Karpenter
                coverage. The remaining {fmt(potential.savingsMonthly - estimate.savingsMonthly)}/mo sits on
                nodes no autoscaler manages yet — OptOps brings them under Karpenter, then live-migrates
                their workloads with no downtime.
              </p>
            </div>
          )}

          {/* Capacity-policy comparison — what each purchasing mode would cost
              on THIS cluster's workloads. Lets the user pick a savings/risk
              point instead of taking one number on faith. */}
          {policies && (
            <div className="mt-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                By capacity policy
              </p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
                      <th className="py-1.5 pr-3 font-medium">Policy</th>
                      <th className="py-1.5 pr-3 font-medium text-right tabular-nums">Optimized</th>
                      <th className="py-1.5 font-medium text-right tabular-nums">Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policies.map((p) => (
                      <tr key={p.key} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-2 pr-3">
                          <span className="font-medium text-slate-900 dark:text-slate-100">{p.label}</span>
                          <span className="block text-xs text-slate-500 dark:text-slate-400">{p.blurb}</span>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-slate-900 dark:text-slate-100">
                          {fmt(p.optimizedMonthly)}/mo
                        </td>
                        {/* A policy can legitimately cost MORE (on-demand on an
                            all-spot fleet is a reliability choice, not a saving).
                            Render that as a cost increase — "savings −$315 / −0%"
                            is nonsense the reader has to decode. */}
                        {p.savingsMonthly > 0 ? (
                          <td className="py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                            {fmt(p.savingsMonthly)}/mo
                            <span className="block text-xs text-slate-500 dark:text-slate-400">
                              −{Math.round(p.savingsPct)}%
                            </span>
                          </td>
                        ) : (
                          <td className="py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">
                            +{fmt(Math.abs(p.savingsMonthly))}/mo
                            <span className="block text-xs text-slate-400 dark:text-slate-500">
                              costs more
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            Covers all {estimate.currentNodes || 0} nodes the agent can see — the fleet table below prices
            only the Karpenter-managed ones, so the two totals differ.{' '}
            Simulated from your live workloads and real cloud prices, refreshed every ~10 minutes.{' '}
            <span className="font-medium text-slate-900 dark:text-slate-200">Enable the in-cluster engine to apply this with one click</span> — until then nothing is changed.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Engine absent AND no usable estimate: we genuinely do not know the
  // headroom yet. Falling through to the main render would hit the `optimal`
  // branch (backend.available is false with no engine) and state
  // "Optimally packed on spot" — a confident verdict that is flatly wrong on
  // an overprovisioned cluster, and unfalsifiable to the user. Say we don't
  // know instead, and still show the one number we do trust.
  if (engineMissing && !estimate && !noData) {
    return (
      <Card className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Available savings</p>
            <button onClick={fetchPlan} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="mt-1 text-2xl font-semibold text-slate-400 dark:text-slate-500">Analyzing your fleet</p>
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50 sm:max-w-xs">
            <p className="text-xs text-slate-500 dark:text-slate-400">Current cost</p>
            <p className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {fmt(currentMonthly)}<span className="text-sm font-normal text-slate-500 dark:text-slate-400">/mo</span>
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
              <Server className="h-3 w-3" />{currentNodes} nodes
            </p>
          </div>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            We haven’t finished measuring this cluster’s optimization headroom — this usually takes a few minutes
            after the agent starts reporting. We’d rather say nothing than show you a number we can’t stand behind.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Available savings</p>
            {noData ? (
              <p className="mt-1 text-3xl font-semibold text-slate-400 dark:text-slate-500">
                Syncing fleet data
              </p>
            ) : optimal ? (
              // Optimally packed — but never a bare "$0". Lead with the value
              // the customer is banking against the on-demand baseline (the
              // spot discount we maintain), which is large even at optimal.
              spotSavingsMonthly > 0 ? (
                <p className="mt-1 text-5xl font-semibold tabular-nums text-emerald-600 flex items-baseline gap-2">
                  {fmt(spotSavingsMonthly)}<span className="text-lg font-normal text-slate-500 dark:text-slate-400">/mo</span>
                  <span className="text-sm font-normal text-slate-500 dark:text-slate-400">vs on-demand</span>
                  {spotSavingsPct >= 1 && (
                    <Badge className="ml-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs flex items-center gap-1">
                      <TrendingDown className="h-3 w-3" />{spotSavingsPct.toFixed(0)}%
                    </Badge>
                  )}
                </p>
              ) : (
                <p className="mt-1 text-3xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <CheckCircle className="h-7 w-7 text-emerald-600" />
                  Cluster is right-sized
                </p>
              )
            ) : (
              <p className="mt-1 text-5xl font-semibold tabular-nums text-emerald-600 flex items-baseline gap-2">
                {fmt(savingsMonthly)}<span className="text-lg font-normal text-slate-500 dark:text-slate-400">/mo</span>
                {Math.abs(savingsPct) >= 1 && (
                  <Badge className="ml-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />{Math.abs(savingsPct).toFixed(0)}%
                  </Badge>
                )}
              </p>
            )}
            {clusterOptimum?.managed_optimal_monthly_usd > 0 && (
              // Managed scope only — same convention as the fleet card
              // (Karpenter nodes, unmanaged/MNG excluded), so this number
              // reconciles with Current/Optimized cost above it.
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 tabular-nums">
                Cluster optimum {fmt(clusterOptimum.managed_optimal_monthly_usd)}/mo
                {clusterOptimum.gap_monthly_usd > 1 && (
                  <span> · {fmt(clusterOptimum.gap_monthly_usd)} above optimum</span>
                )}
              </p>
            )}
          </div>
          <button onClick={fetchPlan} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* current ──▶ optimized */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
            <p className="text-xs text-slate-500 dark:text-slate-400">Current cost</p>
            <p className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">{noData ? '—' : fmt(currentMonthly)}{!noData && <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/mo</span>}</p>
            {!noData && <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5"><Server className="h-3 w-3" />{currentNodes} nodes</p>}
          </div>
          <ArrowRight className="h-6 w-6 text-slate-400 mx-auto hidden sm:block" />
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
            <p className="text-xs text-slate-500 dark:text-slate-400">Optimized cost</p>
            <p className="text-2xl font-semibold tabular-nums text-emerald-600">{noData ? '—' : fmt(optimizedMonthly)}{!noData && <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/mo</span>}</p>
            {!noData && <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5"><Server className="h-3 w-3" />{optimizedNodes} nodes</p>}
          </div>
        </div>

        {/* Plan source line — technical detail (reason / plan name) lives in the tooltip only. */}
        <p
          className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1"
          title={[engine?.reason, backend?.plan_name].filter(Boolean).join(' — ') || undefined}
        >
          <Zap className="h-3 w-3" />
          {noData
            ? 'Fleet data is syncing — this usually takes under a minute.'
            : optimal
              ? (backend?.phase === 'Failed'
                  ? 'Last rebalance didn’t complete — it will retry automatically.'
                  : spotSavingsMonthly > 0
                    ? 'Optimally packed on spot — you’re banking this vs on-demand, and we keep it that way as your workloads change.'
                    : 'Your cluster is right-sized — we keep checking every few minutes.')
              : 'Based on the live plan computed in your cluster.'}
        </p>

        {/* CTA — only the engine path (RebalanceClaim). No backend "run optimizer". */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={applyRebalance}
            disabled={applying || optimal || noData}
            title={noData ? 'Waiting for fleet data' : optimal ? 'Nothing to apply — your cluster is already right-sized' : 'Move workloads to the optimized node set'}
            className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {applying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Apply rebalance
          </button>
          {msg && (
            <span className={`text-xs ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`} title={msg.detail || undefined}>
              {msg.text}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AvailableSavingsHero;
